(() => {
  'use strict';

  const PREFIX = '[FTR Loop Cart Presentation]';
  let refreshTimer = null;
  let drawerObserver = null;
  const productImagePromises = new Map();

  function readStorage(key) {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  }

  function writeStorage(key, value) {
    if (!value) return;
    try { localStorage.setItem(key, value); } catch (_) {}
    try { sessionStorage.setItem(key, value); } catch (_) {}
  }

  function getStoredMeta(bundleId) {
    const key = `ftr-loop-bundle-meta:${bundleId}`;
    const raw = readStorage(key);
    if (!raw) return {};
    try { return JSON.parse(raw) || {}; } catch (_) { return {}; }
  }

  function persistMeta(bundleId, meta) {
    if (!bundleId || !meta) return;
    const clean = {
      title: String(meta.title || '').trim(),
      url: String(meta.url || '').trim(),
      image: String(meta.image || '').trim()
    };
    try {
      const raw = JSON.stringify(clean);
      localStorage.setItem(`ftr-loop-bundle-meta:${bundleId}`, raw);
      sessionStorage.setItem(`ftr-loop-bundle-meta:${bundleId}`, raw);
    } catch (_) {}
    if (clean.url) writeStorage(`ftr-loop-bundle-url:${bundleId}`, clean.url);
    if (clean.title) writeStorage(`ftr-loop-bundle-name:${bundleId}`, clean.title);
    if (clean.image) writeStorage(`ftr-loop-bundle-image:${bundleId}`, clean.image);
  }

  function getBundleId(item) {
    return item?.properties?._bundleId ? String(item.properties._bundleId) : '';
  }

  function getBundleGroups(cart) {
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      const id = getBundleId(item);
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    });
    return groups;
  }

  function getGroupMeta(bundleId, items) {
    const source = (items || []).find((item) => {
      const p = item?.properties || {};
      return p._bundleProductUrl || p._bundleProductImage || p._bundleProductTitle;
    }) || items?.[0] || {};
    const p = source.properties || {};
    const stored = getStoredMeta(bundleId);

    const meta = {
      title: String(p._bundleProductTitle || stored.title || p._bundleName || readStorage(`ftr-loop-bundle-name:${bundleId}`) || 'Bundle').trim(),
      url: String(p._bundleProductUrl || stored.url || readStorage(`ftr-loop-bundle-url:${bundleId}`) || '').trim(),
      image: String(p._bundleProductImage || stored.image || readStorage(`ftr-loop-bundle-image:${bundleId}`) || '').trim()
    };

    persistMeta(bundleId, meta);
    return meta;
  }

  function normalizeProductJsonUrl(productUrl) {
    if (!productUrl) return '';
    try {
      const url = new URL(productUrl, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return '';
      return `${window.Shopify?.routes?.root || '/'}products/${match[1]}.js`;
    } catch (_) {
      return '';
    }
  }

  function normalizeImage(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || '';
  }

  async function resolveParentImage(bundleId, meta) {
    const cacheKey = `ftr-loop-bundle-parent-image:${bundleId}`;
    const cached = readStorage(cacheKey);
    if (cached) {
      meta.image = cached;
      return meta;
    }

    const productJsonUrl = normalizeProductJsonUrl(meta.url);
    if (!productJsonUrl) return meta;

    if (!productImagePromises.has(productJsonUrl)) {
      productImagePromises.set(productJsonUrl, fetch(productJsonUrl, {
        credentials: 'same-origin',
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Parent product lookup failed (${response.status})`);
        const product = await response.json();
        return normalizeImage(product?.featured_image) || normalizeImage(product?.images?.[0]);
      }).catch((error) => {
        console.warn(PREFIX, 'Unable to resolve parent product image.', error);
        return '';
      }));
    }

    const image = await productImagePromises.get(productJsonUrl);
    if (image) {
      meta.image = image;
      writeStorage(cacheKey, image);
      writeStorage(`ftr-loop-bundle-image:${bundleId}`, image);
      persistMeta(bundleId, meta);
    }
    return meta;
  }

  function effectiveLinePrice(item) {
    const quantity = Math.max(1, Number(item?.quantity || 1));
    const finalLine = Number(item?.final_line_price || 0);
    const allocationUnit = Number(item?.selling_plan_allocation?.price || 0);
    const allocationLine = allocationUnit > 0 ? allocationUnit * quantity : 0;
    if (finalLine > 0 && allocationLine > 0) return Math.min(finalLine, allocationLine);
    return allocationLine || finalLine || 0;
  }

  function compareLinePrice(item) {
    const quantity = Math.max(1, Number(item?.quantity || 1));
    const current = effectiveLinePrice(item);
    const compareUnit = Number(item?.selling_plan_allocation?.compare_at_price || 0);
    const compareLine = compareUnit > 0 ? compareUnit * quantity : 0;
    const originalLine = Number(item?.original_line_price || item?.compare_at_line_price || 0);
    return Math.max(current, compareLine, originalLine);
  }

  function bundleTotals(items) {
    return (items || []).reduce((totals, item) => {
      totals.final += effectiveLinePrice(item);
      totals.compare += compareLinePrice(item);
      return totals;
    }, { final: 0, compare: 0 });
  }

  function formatMoney(cents) {
    const cfg = window.FTRCartConfig || {};
    const fmt = cfg.moneyFormat || '${{amount}}';
    const number = Number(cents || 0);
    const amount = (number / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(number / 100));
    const amountComma = amount.replace('.', ',');
    const amountNoDecimalsComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{[\s]*amount_no_decimals_with_comma_separator[\s]*\}\}/g, amountNoDecimalsComma)
      .replace(/\{\{[\s]*amount_with_comma_separator[\s]*\}\}/g, amountComma)
      .replace(/\{\{[\s]*amount_no_decimals[\s]*\}\}/g, amountNoDecimals)
      .replace(/\{\{[\s]*amount[\s]*\}\}/g, amount);
  }

  function buildPriceHtml(items) {
    const totals = bundleTotals(items);
    if (totals.compare > totals.final) {
      return `<s class="ftr-loop-bundle-price__compare">${formatMoney(totals.compare)}</s> <span>${formatMoney(totals.final)}</span>`;
    }
    return `<span>${formatMoney(totals.final)}</span>`;
  }

  function ensureStyles() {
    if (document.getElementById('ftr-loop-cart-presentation-styles')) return;
    const style = document.createElement('style');
    style.id = 'ftr-loop-cart-presentation-styles';
    style.textContent = `
      #ftr-cart-drawer .ftr-loop-bundle__parent-link { color: inherit; text-decoration: none; }
      #ftr-cart-drawer .ftr-loop-bundle__parent-link:hover { text-decoration: underline; }
      #ftr-cart-drawer .ftr-loop-bundle__parent-image-link { display: block; align-self: start; line-height: 0; }
      #ftr-cart-drawer .ftr-loop-bundle__parent-image-link:hover { text-decoration: none; }
      #ftr-cart-drawer .ftr-loop-bundle .ftr-cart-item__price { white-space: nowrap; }
      #ftr-cart-drawer .ftr-loop-bundle-price__compare { color: #999; font-weight: 400; font-size: 12px; margin-right: 4px; }
    `;
    document.head.appendChild(style);
  }

  function setImageLink(image, meta) {
    if (!image) return;
    if (meta.image) {
      image.src = meta.image;
      image.alt = meta.title || 'Bundle';
      image.removeAttribute('srcset');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
    }

    const currentLink = image.closest('a');
    if (meta.url) {
      if (currentLink) {
        currentLink.href = meta.url;
        currentLink.classList.add('ftr-loop-bundle__parent-link', 'ftr-loop-bundle__parent-image-link');
      } else {
        const link = document.createElement('a');
        link.href = meta.url;
        link.className = 'ftr-loop-bundle__parent-link ftr-loop-bundle__parent-image-link';
        image.parentNode?.insertBefore(link, image);
        link.appendChild(image);
      }
    }
  }

  function setTitleLink(title, meta) {
    if (!title) return;
    const existing = title.querySelector('a');
    const label = meta.title || title.textContent.trim() || 'Bundle';
    if (meta.url) {
      if (existing) {
        existing.href = meta.url;
        existing.textContent = label;
        existing.className = 'ftr-loop-bundle__parent-link';
      } else {
        const link = document.createElement('a');
        link.href = meta.url;
        link.className = 'ftr-loop-bundle__parent-link';
        link.textContent = label;
        title.replaceChildren(link);
      }
    } else {
      title.textContent = label;
    }
  }

  async function fixDrawer(cart, groups) {
    const drawer = document.getElementById('ftr-cart-drawer');
    if (!drawer) return;

    await Promise.all(Array.from(drawer.querySelectorAll('.ftr-loop-bundle[data-loop-bundle-id]')).map(async (card) => {
      const bundleId = String(card.dataset.loopBundleId || '');
      const items = groups.get(bundleId) || [];
      if (!items.length) return;

      const meta = await resolveParentImage(bundleId, getGroupMeta(bundleId, items));
      const image = card.querySelector('.ftr-cart-item__img');
      const title = card.querySelector('.ftr-cart-item__title');
      const price = card.querySelector('.ftr-cart-item__price');
      const edit = card.querySelector('.ftr-loop-bundle__edit');

      setImageLink(image, meta);
      setTitleLink(title, meta);

      if (edit) {
        if (meta.url) {
          edit.href = meta.url;
          edit.textContent = 'View bundle';
          edit.hidden = false;
        } else {
          edit.hidden = true;
        }
      }

      if (price) price.innerHTML = buildPriceHtml(items);
      card.dataset.ftrParentBundleFixed = 'true';
    }));
  }

  async function fixCartPage(groups) {
    const rows = document.querySelectorAll('.ftr-loop-bundle-header-row[data-bundle-id]');
    await Promise.all(Array.from(rows).map(async (row) => {
      const bundleId = String(row.dataset.bundleId || '');
      const items = groups.get(bundleId) || [];
      if (!items.length) return;

      const meta = await resolveParentImage(bundleId, getGroupMeta(bundleId, items));
      const image = row.querySelector('.ftr-loop-bundle-header__image');
      const media = row.querySelector('.ftr-loop-bundle-header__media');
      const title = row.querySelector('.ftr-loop-bundle-header__title');

      if (image && meta.image) {
        image.src = meta.image;
        image.alt = meta.title || 'Bundle';
        image.removeAttribute('srcset');
      }
      if (media && meta.url) media.href = meta.url;
      setTitleLink(title, meta);
      row.dataset.ftrParentBundleFixed = 'true';
    }));
  }

  async function refreshPresentation() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js?_ftr_bundle_presentation=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      if (!response.ok) throw new Error('Unable to load cart.');
      const groups = getBundleGroups(cart);
      await Promise.all([fixDrawer(cart, groups), fixCartPage(groups)]);
    } catch (error) {
      console.error(PREFIX, error);
    }
  }

  function scheduleRefresh(delay = 30) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshPresentation, delay);
  }

  function watchDrawer() {
    const inner = document.getElementById('ftr-cart-inner');
    if (!inner || drawerObserver) return;
    drawerObserver = new MutationObserver(() => scheduleRefresh(20));
    drawerObserver.observe(inner, { childList: true });
  }

  function init() {
    ensureStyles();
    watchDrawer();
    scheduleRefresh(0);
    document.addEventListener('on:cart:add', () => scheduleRefresh(100));
    document.addEventListener('on:line-item:change', () => scheduleRefresh(100));
    document.addEventListener('ftr:cart:updated', () => scheduleRefresh(100));
    window.addEventListener('pageshow', () => scheduleRefresh(30));
    window.setTimeout(() => scheduleRefresh(0), 300);
    window.setTimeout(() => scheduleRefresh(0), 900);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
